import type { CSSProperties } from 'react';
import ui from './ui.module.css';

const pct = (f: number) => `${Math.max(0, Math.min(100, f * 100)).toFixed(1)}%`;

interface BarProps {
  /** 0–1. */
  value: number;
  height?: number;
  label?: string;
  className?: string;
  glow?: boolean;
}

function trackStyle(height: number): CSSProperties {
  return { height, borderRadius: height / 2 };
}

/** The level bar: amber gradient. */
export function XpBar({ value, height = 8, label, className }: BarProps) {
  return (
    <div
      className={[ui.track, className ?? ''].join(' ')}
      style={trackStyle(height)}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
    >
      <div className={ui.fillXp} style={{ width: pct(value) }} />
    </div>
  );
}

/** The thinner skill point bar: green. */
export function SpBar({ value, height = 4, label, className, glow }: BarProps) {
  return (
    <div
      className={[ui.track, className ?? ''].join(' ')}
      style={trackStyle(height)}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
    >
      <div className={ui.fillSp} style={{ width: pct(value), boxShadow: glow ? '0 0 8px rgba(127,214,106,.5)' : undefined }} />
    </div>
  );
}

/** A bar with what you had (dim) and what you just gained (bright), as on the Result screen. */
export function SplitBar({
  prev,
  gain,
  kind,
  height,
  label,
  className,
}: {
  prev: number;
  gain: number;
  kind: 'xp' | 'sp';
  /** Height in px; leave out to size it with `className`. */
  height?: number;
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={[ui.track, className ?? ''].join(' ')}
      style={height === undefined ? undefined : trackStyle(height)}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round((prev + gain) * 100)}
    >
      <div className={kind === 'xp' ? ui.fillPrev : ui.fillSpPrev} style={{ width: pct(prev) }} />
      <div className={kind === 'xp' ? ui.fillGain : ui.fillSpGain} style={{ width: pct(gain) }} />
    </div>
  );
}
